import { GeocodeRequester, type LatLng } from '@cobliteam/api-lib';
import type { DeviceFragment } from '@cobliteam/api-lib/src/__generated__/graphql';

import { Haversine } from '@cobliteam/common-types';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import { Environment } from '@cobliteam/react-environments';
import { loadEtaRouteFailed, loadEtaRouteSucceeded, loadEtaVehiclesFailed, loadEtaVehiclesSucceeded } from '@cobliteam/store/src/VehiclesTab/actionCreators';
import type { LoadEtaRouteRequest, LoadEtaVehiclesRequest } from '@cobliteam/store/src/VehiclesTab/actions';
import { ActionTypes } from '@cobliteam/store/src/VehiclesTab/actionTypes';
import type { DeviceDistance, EtaVehicle } from '@cobliteam/store/src/VehiclesTab/types';
import Alert from '@cobliteam/ui/components/Alert';
import { all, call, put, takeLatest } from 'redux-saga/effects';

const env = Environment.getInstance().getEnvironment();
const geocodeRequester = new GeocodeRequester({ host: env.herbieApiHost });

const getDistanceMatrix = (googlePayload: google.maps.DistanceMatrixRequest) =>
  new Promise((resolve, reject) => {
    const service = new google.maps.DistanceMatrixService();

    service.getDistanceMatrix(googlePayload, (response, status) => {
      if (status === google.maps.DistanceMatrixStatus.OK) {
        const responseRows = response?.rows.filter((row) => row.elements[0].status === google.maps.DistanceMatrixElementStatus.OK);

        const etaArray: google.maps.DistanceMatrixResponseElement[] | undefined = responseRows?.map((row) => {
          const eta = row.elements[0];

          eta.duration.value = eta.duration_in_traffic ? eta.duration_in_traffic.value : eta.duration.value;

          return eta;
        });

        resolve(etaArray);
      } else {
        reject(response);
      }
    });
  });

const getDistanceEtaRoute = (googlePayload: google.maps.DirectionsRequest) =>
  new Promise((resolve, reject) => {
    const directionsService = new google.maps.DirectionsService();

    directionsService.route(googlePayload, (response, status) => {
      if (status === google.maps.DirectionsStatus.OK) {
        resolve(response?.routes[0].legs[0]);
      } else {
        reject(response);
      }
    });
  });

// `destination` aceita o ponto quando ele já é conhecido: traçar a rota até o texto reintroduz
// a ambiguidade do geocoder num logradouro que se repete na cidade.
export const getEtaRoute = (vehicleLocation: LatLng, destination: string | LatLng) =>
  getDistanceEtaRoute({
    origin: vehicleLocation,
    destination,
    travelMode: google.maps.TravelMode.DRIVING,
    drivingOptions: {
      departureTime: new Date(),
      trafficModel: google.maps.TrafficModel.BEST_GUESS,
    },
  });

const sortVehiclesByEtaDuration = (vehicle1: EtaVehicle, vehicle2: EtaVehicle) => {
  const vehicle1Duration = vehicle1.eta.duration_in_traffic ? vehicle1.eta.duration_in_traffic.value : vehicle1.eta.duration.value;
  const vehicle2Duration = vehicle2.eta.duration_in_traffic ? vehicle2.eta.duration_in_traffic.value : vehicle2.eta.duration.value;

  return vehicle1Duration - vehicle2Duration;
};

export const getEtaVehicles = (vehiclesLocations: DeviceDistance[], destination: string | LatLng) =>
  getDistanceMatrix({
    origins: vehiclesLocations,
    destinations: [destination],
    travelMode: google.maps.TravelMode.DRIVING,
    drivingOptions: {
      departureTime: new Date(),
    },
  });

// The list must have a maximum of 25 items because google has a limit for this service call
const DEVICES_LIMIT = 25;

export const getVehiclesLocationsList = (devices: DeviceFragment[], destination: google.maps.LatLngLiteral): DeviceDistance[] => {
  const filteredDevices = devices.filter((device) => device.lastLocation && Object.keys(device.lastLocation).length > 0);

  const nearestDevices = filteredDevices.map((device) => {
    const coordinate = {
      lat: device.lastLocation?.latitude || 0,
      lng: device.lastLocation?.longitude || 0,
    };

    const distance = Haversine.kmDistanceBetweenPoints(coordinate, destination);

    return {
      distance,
      ...coordinate,
      esn: device.id,
    };
  });

  const sortedDevices = nearestDevices.sort((device1, device2) => device1.distance - device2.distance);

  return sortedDevices.slice(0, DEVICES_LIMIT);
};

// Creates a new list with the device id and the ETA data
export const getVehicles = (devices: DeviceDistance[], etaList: google.maps.DistanceMatrixResponseElement[], destination: string): EtaVehicle[] =>
  devices
    .map((device, index) => ({
      esn: device.esn,
      eta: etaList[index],
      destination,
    }))
    .sort(sortVehiclesByEtaDuration);

export function* requestLoadEtaVehiclesSaga(action: LoadEtaVehiclesRequest) {
  try {
    const { address, devices, coordinate } = action.payload;

    // Ponto conhecido na origem (local de interesse cadastrado, candidato do autocomplete
    // self-hosted) manda; o geocoding por texto só entra quando ele falta. Distância e ETA
    // usam o mesmo ponto — resolver o texto de novo aqui traria de volta o trecho errado.
    const destinationLatLng: LatLng = coordinate ?? (yield call(geocodeRequester.geocodeFromAddress, address));
    const vehiclesLocations: DeviceDistance[] = yield call(getVehiclesLocationsList, devices, destinationLatLng);
    const etaVehicles: google.maps.DistanceMatrixResponseElement[] = yield call(getEtaVehicles, vehiclesLocations, coordinate ?? address);
    const vehiclesEta: EtaVehicle[] = yield call(getVehicles, vehiclesLocations, etaVehicles, address);

    const destination = { address, ...destinationLatLng };

    const filteredVehiclesEta = vehiclesEta.filter((vehicleEta) => vehicleEta.eta);

    yield put(loadEtaVehiclesSucceeded(destination, filteredVehiclesEta));
  } catch (err) {
    DatadogTrack.error(err as Error, { EventSource: 'VehiclesTabSaga' });
    yield put(loadEtaVehiclesFailed());
    Alert.error('Houve um erro ao carregar os veículos, tente novamente mais tarde.');
  }
}

export function* requestLoadEtaRouteSaga(action: LoadEtaRouteRequest) {
  try {
    const { address, device, coordinate } = action.payload;

    const { lastLocation } = device;

    if (lastLocation?.latitude && lastLocation?.longitude) {
      const { latitude, longitude } = lastLocation;

      const latLng: LatLng = {
        lat: latitude,
        lng: longitude,
      };

      const route: google.maps.DirectionsRoute = yield call(getEtaRoute, latLng, coordinate ?? address);

      yield put(loadEtaRouteSucceeded(device, route));
    }
  } catch (err) {
    DatadogTrack.error(err as Error, { EventSource: 'VehiclesTabSaga' });
    yield put(loadEtaRouteFailed());
    Alert.error('Houve um erro ao carregar a rota, tente novamente mais tarde.');
  }
}

export function* VehiclesTabSagasListener() {
  yield all([takeLatest(ActionTypes.LOAD_ETA_VEHICLES_REQUEST, requestLoadEtaVehiclesSaga), takeLatest(ActionTypes.LOAD_ETA_ROUTE_REQUEST, requestLoadEtaRouteSaga)]);
}
